import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminMobileMenuButton from 'components/admin/AdminMobileMenuButton';
import { DataTableTheme } from 'components/shared/DataTable';
import DataTable from 'components/shared/DataTable';
import Menu from 'components/shared/menu';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminFlagsQueryKeys, adminIndexFlags, adminUpdateFlag, FlagOptions } from 'services/api/admin/flags';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';
import EllipsisIcon from 'svg/EllipsisIcon';
import EyeIcon from 'svg/EyeIcon';
import PencilIcon from 'svg/PencilIcon';
import PublishIconOutlined from 'svg/PublishIconOutlined';

import DisableFlagModal from './DisableFlagModal';
import EnableFlagModal from './EnableFlagModal';

const TABS: readonly TabType[] = [
  { label: 'Client Flags', value: 'client' },
  { label: 'Global Flags', value: 'global' },
] as const;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function listOf(items: string[]) {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0];
}

const usersOf = (flag: FlagOptions) => flag.users ?? [];

const userCount = (flag: FlagOptions) => (usersOf(flag).length > 0 ? plural(usersOf(flag).length, 'user') : '');

// Flipper's on/off switch for everyone deletes every user, client and group on the flag, so the
// confirm modals name what will go.
function removedTargets(flag: FlagOptions) {
  const parts = [
    userCount(flag),
    flag.clients.length > 0 && plural(flag.clients.length, 'client'),
    flag.groups.length > 0 && `the ${listOf(flag.groups)} ${flag.groups.length === 1 ? 'group' : 'groups'}`,
  ].filter(Boolean) as string[];
  return parts.length > 0 ? listOf(parts) : undefined;
}

function FlagActionMenu({ children }: { children: React.ReactNode }) {
  return (
    <ActionCell>
      <Menu>
        <Menu.Trigger>
          <MenuTriggerButton>
            <EllipsisIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
          </MenuTriggerButton>
        </Menu.Trigger>
        <Menu.Content position='right' style={{ padding: 0, minWidth: 200 }}>
          {children}
        </Menu.Content>
      </Menu>
    </ActionCell>
  );
}

export default function FeatureFlags() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const canEdit = profile.has(PERMISSIONS.adminFeatureFlagEdit);
  // The tab lives in the URL so the edit page can send you back to the one you came from.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = TABS.find((t) => t.value === searchParams.get('tab')) ?? TABS[0];
  const setSelectedTab = (tab: TabType) => setSearchParams({ tab: tab.value }, { replace: true });
  const [modalFlag, setModalFlag] = useState<FlagOptions | null>(null);
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const { data = [], isLoading } = useQuery({
    queryKey: adminFlagsQueryKeys.index(),
    queryFn: () => adminIndexFlags(),
  });

  const globalToggleMutation = useMutation({
    mutationFn: ({ flag, enabled }: { flag: FlagOptions; enabled: boolean }) =>
      adminUpdateFlag(flag.name, { name: flag.name, globallyEnabled: enabled }),
    onSuccess: (_data, { enabled }) => {
      queryClient.invalidateQueries({ queryKey: adminFlagsQueryKeys.index() });
      addToast({ text: enabled ? 'Feature flag enabled for all users' : 'Feature flag disabled for all users' });
      setModalFlag(null);
    },
    onError: () => {
      addToast({ text: 'Something went wrong. Please try again.' });
    },
  });

  const globalFlags = React.useMemo(() => data.filter((f) => f.category === 'global'), [data]);
  const clientFlags = React.useMemo(() => data.filter((f) => f.category === 'client'), [data]);

  const columnHelper = createColumnHelper<FlagOptions>();

  const nameColumn = columnHelper.accessor('title', {
    header: 'Feature Name',
    cell: (info) => <BodySmallBold>{info.getValue()}</BodySmallBold>,
  });

  const editMenuItem = (flag: FlagOptions) => (
    <Menu.Item
      style={{ padding: '12px', fontSize: '14px' }}
      onClick={() => navigate(`/admin/feature-flags/${flag.name}/edit?tab=${flag.category ?? 'client'}`)}>
      <MenuIconWrapper>
        {canEdit ? <PencilIcon width={16} height={16} /> : <EyeIcon width={16} height={16} />}
      </MenuIconWrapper>
      {canEdit ? 'Edit' : 'View'}
    </Menu.Item>
  );

  const globalColumns = React.useMemo(
    () => [
      nameColumn,
      columnHelper.accessor((row) => row, {
        id: 'enabledFor',
        header: 'Enabled For',
        enableSorting: false,
        cell: (info) => {
          const flag = info.getValue();
          if (flag.globallyEnabled) return <EnabledBadge $variant='global'>Everyone</EnabledBadge>;
          const users = userCount(flag);
          if (flag.groups.length === 0 && !users) return <span>—</span>;
          return (
            <EnabledFor>
              {flag.groups.length > 0 && <EnabledBadge $variant='groups'>{flag.groups.join(', ')}</EnabledBadge>}
              {users && <EnabledBadge $variant='users'>{users}</EnabledBadge>}
            </EnabledFor>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 60,
        cell: (info) => {
          const flag = info.getValue();
          return (
            <FlagActionMenu>
              {editMenuItem(flag)}
              {canEdit && (
                <Menu.Item style={{ padding: '12px', fontSize: '14px' }} onClick={() => setModalFlag(flag)}>
                  <MenuIconWrapper>
                    <PublishIconOutlined size={16} />
                  </MenuIconWrapper>
                  {flag.globallyEnabled ? 'Disable for Everyone' : 'Enable for Everyone'}
                </Menu.Item>
              )}
            </FlagActionMenu>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit]
  );

  const perClientColumns = React.useMemo(
    () => [
      nameColumn,
      columnHelper.accessor('clients', {
        header: 'Clients',
        enableSorting: false,
        cell: (info) => {
          const names = [...new Set(info.getValue().map((c) => c.name))].join(', ');
          return <span>{names || '—'}</span>;
        },
      }),
      columnHelper.accessor((row) => row, {
        id: 'users',
        header: 'Users',
        enableSorting: false,
        cell: (info) => <span>{userCount(info.getValue()) || '—'}</span>,
      }),
      columnHelper.accessor((row) => row, {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 60,
        cell: (info) => <FlagActionMenu>{editMenuItem(info.getValue())}</FlagActionMenu>,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit]
  );

  const isGlobalTab = selectedTab.value === 'global';
  const activeData = isGlobalTab ? globalFlags : clientFlags;
  const activeColumns = isGlobalTab ? globalColumns : perClientColumns;

  return (
    <PageContainer>
      <HeaderWrapper>
        <HeaderSection>
          <Header>
            <TitleRow>
              <AdminMobileMenuButton />
              <Title>Feature Flags</Title>
            </TitleRow>
          </Header>
        </HeaderSection>
        <TabBar>
          <Tabs tabs={TABS} selectedTab={selectedTab} onTabClick={setSelectedTab} />
        </TabBar>
      </HeaderWrapper>

      <TableSection>
        {isLoading ? (
          <p>Loading...</p>
        ) : activeData.length > 0 ? (
          <DataTable
            columns={activeColumns}
            data={activeData}
            defaultSortBy={[{ id: 'title', desc: false }]}
            theme={DataTableTheme.OPEN}
          />
        ) : (
          <EmptyMessage>{isGlobalTab ? 'No global flags' : 'No client flags'}</EmptyMessage>
        )}
      </TableSection>

      {modalFlag && !modalFlag.globallyEnabled && (
        <EnableFlagModal
          flagTitle={modalFlag.title}
          removing={removedTargets(modalFlag)}
          loading={globalToggleMutation.isPending}
          onConfirm={() => globalToggleMutation.mutate({ flag: modalFlag, enabled: true })}
          onCancel={() => setModalFlag(null)}
        />
      )}

      {modalFlag && modalFlag.globallyEnabled && (
        <DisableFlagModal
          flagTitle={modalFlag.title}
          removing={removedTargets(modalFlag)}
          loading={globalToggleMutation.isPending}
          onConfirm={() => globalToggleMutation.mutate({ flag: modalFlag, enabled: false })}
          onCancel={() => setModalFlag(null)}
        />
      )}
    </PageContainer>
  );
}

const PageContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: auto;
`;

const HeaderWrapper = styled.div`
  background: white;
`;

const HeaderSection = styled.div`
  padding: 24px 24px 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-top: 1px solid ${colors.black10};
`;

const TableSection = styled.div`
  padding: 24px;

  table {
    font-size: 14px;
  }

  /* Match the page background for the header row */
  thead {
    background-color: transparent;
  }

  thead td {
    background-color: transparent;
    background-image: none;
  }
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  color: ${colors.black50};
  margin: 0;
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${colors.black50};

  &:hover {
    background: ${colors.black5};
  }
`;

const MenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  margin-right: 8px;
  color: ${colors.black50};
`;

const enabledVariants = {
  global: { bg: colors.green10, color: colors.green100 },
  groups: { bg: colors.yellow10, color: colors.yellow100 },
  users: { bg: colors.blue10, color: colors.blue100 },
};

const EnabledFor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EnabledBadge = styled.span<{ $variant: keyof typeof enabledVariants }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $variant }) => enabledVariants[$variant].bg};
  color: ${({ $variant }) => enabledVariants[$variant].color};
`;
